<template>
  <div id="webviewer" ref="viewer" />
</template>

<script>
import WebViewer from '@pdftron/webviewer'

import PDFTronAnnotationsManager from './pdftron-annotations-manager'
import PDFTronAnnotationsListener from './pdftron-annotations-listener'
import {
  ANNOTATION_RECTANGLE_TOOL_BUTTON,
  ANNOTATION_SELECT_TOOL_BUTTON,
} from '../constants/pdf-annotations'
import { SfLog } from '@app/adapters/logger'
import PdftronAiProposalMediator from '@app/application/reconciliation_proposal/mediators/mediator'
import useI18n from '@app/shared/vue/composables/use-I18n'
import debugLog from '@app/utils/debug-log'

const i18n = useI18n()

// Apryse rejects the `WebViewer.Iframe()` promise with this boilerplate message
// when the viewer iframe window becomes inaccessible — which happens when the
// component is torn down / the page is navigated away before async init
// resolves. Our assets are served same-origin, so this is never a genuine
// cross-domain misconfiguration here; it is benign teardown noise. See issue
// 32378.
const APRYSE_CROSS_DOMAIN_ERROR = 'is on a different domain'

const isApryseCrossDomainError = (error) => {
  const message = typeof error === 'string' ? error : error?.message
  return (
    typeof message === 'string' && message.includes(APRYSE_CROSS_DOMAIN_ERROR)
  )
}

// Locale for Apryse's in-browser office → PDF conversion (xls/xlsx previews
// are loaded from the original file, not the server-converted PDF). Must match
// the server-side conversion locale so ambiguous spreadsheet dates render the
// same in the preview as in the stored PDF and extracted content. Deliberately
// not the user's UI locale: the stored PDF is the same for every viewer.
const OFFICE_CONVERSION_LOCALE = 'en-GB'

export default {
  name: 'PDFTron',
  props: {
    path: { type: String },
    initialDocs: { type: Array },
    ownerId: { type: [Number, String] },
    ownerType: { type: String },
    enableAnnotations: { type: Boolean, required: false, default: false },
    enableAnnotationsToolbar: {
      type: Boolean,
      required: false,
      default: false,
    },
    focusAnnotationFromUrl: { type: Boolean, required: false, default: true },
    showAnnotationBubbles: { type: Boolean, required: false, default: true },
    showCommunicationToggle: { type: Boolean, required: false, default: true },
    fitWidth: { type: Boolean, required: false, default: false },
    disableMouseWheelZoom: { type: Boolean, required: false, default: false },
    enableAiProposalDrawer: { type: Boolean, required: false, default: false },
    hideTabButtons: { type: Boolean, required: false, default: false },
  },

  data() {
    return {
      webViewerInstance: null,
      isUnmounted: false,
    }
  },

  computed: {
    webviewerConfig() {
      const annotationEditingTools = [
        'annotationPopup',
        'textPopup',
        ANNOTATION_SELECT_TOOL_BUTTON,
        ANNOTATION_RECTANGLE_TOOL_BUTTON,
      ]
      const disabledOptionalElements = []

      // 1. Annotations enabled
      if (this.enableAnnotations) {
        // a. disable the header initially, so it doesn't "flash" in its default state (we're modifying some buttons there).it is re-enabled after the PDF has loaded
        disabledOptionalElements.push('header')

        // b. annotations toolbar disabled (e.g. Pulse user): disable all annotation editing tools
        if (!this.enableAnnotationsToolbar) {
          disabledOptionalElements.push(...annotationEditingTools)
        }
      } else {
        // 2. Annotations disabled: disable all annotation editing tools
        disabledOptionalElements.push(...annotationEditingTools)
      }

      return {
        licenseKey:
          'Silverfin NV:OEM:Silverfin Web::B+:AMS(20280430):2B574E031F9794D0F33352184F516F2F2292CC238D7C39869D0C6EF05AFA31F5C7',
        path: this.path,
        ui: 'legacy',
        disabledElements: [
          'panToolButton',
          'leftPanelButton',
          //'viewControlsButton',
          // hiding view toolbar option since only option left
          'toolbarGroup-View',
          // all toolbar options with editing options disabled
          'toolbarGroup-Shapes',
          'toolbarGroup-Insert',
          'toolbarGroup-Edit',
          'toolbarGroup-FillAndSign',
          'toolbarGroup-Forms',
          'toolbarGroup-Annotate',
          // comments
          'toggleNotesButton',
          // dark theme chooser
          'themeChangeButton',
          // thumbnail toolbar
          'thumbnailControl',
          // page setting editing options
          'rotateHeader',
          //'rotateClockwiseButton',
          //'rotateCounterClockwiseButton',
          'toggleCompareModeButton',
          'viewControlsDivider2',
          // text selection popups
          'contextMenuPopup',
          // entire settings menu + search - disabled for now
          'menuButton',
          'searchButton',
          // opens an edit PDF menu with options that interact weirdly with annotations
          'thumbMultiSelect',
          'toolsHeader',
        ].concat(disabledOptionalElements),
        documentXFDFRetriever: async () => {
          if (this.pdfAnnotationsListener) {
            const annotations =
              await this.pdfAnnotationsListener.fetchAnnotations()
            return annotations.map((i) => i.xfdfString)
          } else {
            return []
          }
        },
      }
    },
  },
  async mounted() {
    const rootStyles = getComputedStyle(document.documentElement)
    const documentBackgroundColor = rootStyles
      .getPropertyValue('--pdftron-document-background-color')
      .trim()

    try {
      // WebViewer 11 changed the default constructor to WebComponent (Shadow DOM)
      // mode. Our annotation integration in `pdftron-annotations-ui.js` reaches
      // into `instance.UI.iframeWindow`, so we explicitly opt into iframe mode.
      const webviewer = await WebViewer.Iframe(this.webviewerConfig, this.$el)
      this.webViewerInstance = webviewer

      // Suppress WebViewer's interactive "Sign Here" placeholder. It is drawn
      // over any signature form field WebViewer treats as unsigned — including
      // fields left cryptographically empty by external e-signing tools that
      // already stamped a visible signature. That painted a "Sign Here" box over
      // an already-signed attachment in the preview, while the downloaded
      // original rendered fine (Freshdesk #252356). Our viewer is read-only for
      // signatures (Fill&Sign / Forms / Annotate toolbars are disabled above), so
      // this placeholder is never actionable here — return a hidden element.
      //
      // `visibility` rather than `display`, and hidden rather than merely empty:
      // WebViewer keeps styling the element it is handed, flipping `display`
      // as the field's signature state changes and forcing `background` to
      // darkgrey on a read-only field — which turned the emptied placeholder
      // into a small grey square. It never touches `visibility`.
      //
      // Must run synchronously before WebViewer's annotation-draw pass (do not
      // move it below an `await`, or the box flashes on first render). Uses
      // Apryse's first-party setter (the sanctioned wiring point); the
      // optional-chaining means an Apryse API change can only make the (cosmetic)
      // box reappear rather than throw and abort the rest of viewer init below.
      webviewer.Core?.Annotations?.setCustomCreateSignHereElementHandler?.(
        () => {
          const placeholder =
            webviewer.UI.iframeWindow.document.createElement('div')
          placeholder.style.visibility = 'hidden'
          return placeholder
        }
      )

      // Second half of the same artefact: independently of the placeholder,
      // WebViewer tints every form field with a translucent blue highlight, so
      // a blue rectangle stayed behind over the signature once the "Sign Here"
      // box above stopped covering it. Nothing in this viewer can fill or save
      // form data, so the highlight advertises an affordance that does not
      // exist here. Optional-chained for the same reason as above.
      webviewer.Core.annotationManager
        .getFieldManager()
        .disableWidgetHighlighting?.()

      webviewer.UI.setLanguage(i18n.locale)

      webviewer.UI.iframeWindow.document.documentElement.style.setProperty(
        '--document-background-color',
        documentBackgroundColor
      )

      // To prevent SXSS
      webviewer.Core.disableEmbeddedJavaScript()

      // Multi-tab mode is gated behind a feature flag in WebViewer 10.x; without
      // it, only the first document is rendered and the tab strip is missing or
      // non-functional for additional docs.
      if (this.initialDocs?.length > 1) {
        webviewer.UI.enableFeatures([webviewer.UI.Feature.MultiTab])
      }

      // Documents are loaded here rather than via the constructor's
      // `initialDoc` because office conversion options (the locale) are only
      // accepted at document load. Not awaited: the annotation listeners below
      // must attach while the document is still loading.
      this.loadInitialDocs(webviewer)

      if (this.disableMouseWheelZoom) {
        webviewer.UI.disableFeatures([webviewer.UI.Feature.MouseWheelZoom])
      }

      if (this.enableAiProposalDrawer) {
        this.pdfAiProposalMediator = new PdftronAiProposalMediator(
          webviewer,
          this.initialDocs.length > 1
        )
        this.pdfAiProposalMediator.start()
      }

      if (!this.enableAnnotations) {
        return
      }

      this.pdfAnnotationsManager = new PDFTronAnnotationsManager(
        webviewer,
        this.ownerId,
        this.ownerType,
        {
          enableAnnotationsToolbar: this.enableAnnotationsToolbar,
          focusAnnotationFromUrl: this.focusAnnotationFromUrl,
          showAnnotationBubbles: this.showAnnotationBubbles,
          showCommunicationToggle: this.showCommunicationToggle,
        }
      )

      this.pdfAnnotationsListener = new PDFTronAnnotationsListener(
        webviewer,
        this.pdfAnnotationsManager
      )
      this.pdfAnnotationsListener.setup()
    } catch (error) {
      // Drop benign teardown-race rejections: either the component was
      // unmounted before init resolved, or Apryse rejected with its
      // cross-domain boilerplate (see `isApryseCrossDomainError`). Both flood
      // Sentry with non-actionable noise. Any other failure is a genuine init
      // error and still reported.
      if (this.isUnmounted || isApryseCrossDomainError(error)) {
        debugLog('PDFTron init aborted before completion', error)
        return
      }

      SfLog.error('PDFTron error', {
        error,
      })
    }
  },
  beforeUnmount() {
    this.isUnmounted = true
    this.cleanupWebViewer()
  },

  methods: {
    async loadInitialDocs(webviewer) {
      const options = {
        officeOptions: {
          formatOptions: { locale: OFFICE_CONVERSION_LOCALE },
        },
      }

      try {
        const docs = this.initialDocs ?? []
        if (docs.length > 1) {
          // Create every tab before activating the first: activation starts
          // that document's (possibly slow) office conversion, and the tab
          // strip and the control-button sweep below need all tabs to exist
          // up front.
          const tabIds = []
          for (const doc of docs) {
            tabIds.push(
              await webviewer.UI.TabManager.addTab(doc, {
                ...options,
                setActive: false,
              })
            )
          }
          this.hideTabControlButtons(webviewer)
          await webviewer.UI.TabManager.setActiveTab(tabIds[0])
        } else if (docs.length === 1) {
          await webviewer.UI.loadDocument(docs[0], options)
        }
      } catch (error) {
        if (this.isUnmounted || isApryseCrossDomainError(error)) {
          debugLog('PDFTron document load aborted before completion', error)
          return
        }

        SfLog.error('PDFTron error', {
          error,
        })
      }
    },

    hideTabControlButtons(webviewer) {
      if (!this.hideTabButtons) {
        return
      }

      const tabsHeader =
        webviewer.UI.iframeWindow.document.getElementsByClassName('TabsHeader')

      if (tabsHeader.length > 0) {
        // Hide only the tab *control* buttons (add / close), not the document
        // tabs themselves. WebViewer 11 added an `aria-label` to each document
        // tab button (`.Button.Tab`), so a bare `button[aria-label]` selector,
        // which used to match only the controls on WebViewer 10.x, now also
        // hides the document tabs, leaving the multi-document strip empty.
        tabsHeader[0]
          .querySelectorAll('button[aria-label]:not(.Tab)')
          .forEach((el) => {
            el.style.display = 'none'
          })
      }
    },

    // See https://community.apryse.com/t/i-cant-seem-to-deinstance-the-webviewer-component/5468/5
    async cleanupWebViewer() {
      if (this.pdfAnnotationsListener) {
        this.pdfAnnotationsListener.cleanup()
      }

      if (this.pdfAiProposalMediator) {
        this.pdfAiProposalMediator.stop()
      }

      this.pdfAnnotationsListener = null
      this.pdfAnnotationsManager = null
      this.pdfAiProposalMediator = null

      if (this.webViewerInstance) {
        this.webViewerInstance.UI.dispose()
        this.webViewerInstance = null
      }

      if (this.$el && this.$el.innerHTML) {
        this.$el.innerHTML = '<div id="webviewer" />'
      }
    },
  },
}
</script>
